/**
 * Reading an on-prem install's self-report (ADR-0074 L1).
 *
 * The hub sends `{version, counts}` on every license heartbeat and Control stores it on
 * the license row. These are the pure helpers that turn it into the two things the
 * console shows: the running VERSION and a one-line usage summary.
 *
 * The whole module exists to keep one distinction honest: **"has never reported" is not
 * "reported zero"**. A hub older than L1 (and one whose best-effort collection failed)
 * heartbeats without a report, so a missing counter must render as "—", never as `0` —
 * a fabricated zero on the console reads as a real, alarming number.
 */

import type { LicenseReport, PlatformLicense } from "@/lib/platform";

/** IT labels for the reported counters, in the order the summary lists them. A counter the
 *  console doesn't know about is skipped rather than shown under its raw key. */
const COUNT_LABELS: Array<[key: string, singular: string, plural: string]> = [
  ["workflows", "workflow", "workflow"],
  ["etl_pipelines", "pipeline", "pipeline"],
  ["apps", "app", "app"],
  ["users", "utente", "utenti"],
  ["gateways", "gateway", "gateway"],
  ["app_rows", "riga", "righe"],
  ["executions_month", "esecuzione/mese", "esecuzioni/mese"],
];

/** How many counters the compact one-liner shows before it stops (the row is a summary,
 *  the full report is the detail view's job). */
export const SUMMARY_MAX_COUNTS = 3;

export function reportVersion(report: LicenseReport | null | undefined): string | null {
  const v = report?.version;
  if (typeof v !== "string" || !v.trim()) return null;
  // The release bakes HUB_VERSION from the git ref (`v0.1.26`, leading `v` included), so
  // installations report it WITH the prefix — and the cell that renders this prepends its
  // own `v`, which showed `vv0.1.26` on the superadmin Licenses page. Fielded boxes will
  // keep reporting the prefixed form forever, so the display side owns the normalization.
  return v.trim().replace(/^v/i, "");
}

/**
 * The compact usage line — `"12 workflow · 3 app · 5 utenti"`. Returns `""` when the
 * install reported no counters at all, so the caller renders the "—" placeholder rather
 * than an empty-looking row.
 */
export function reportSummary(
  report: LicenseReport | null | undefined,
  max = SUMMARY_MAX_COUNTS,
): string {
  const counts = report?.counts;
  if (!counts) return "";
  const parts: string[] = [];
  for (const [key, singular, plural] of COUNT_LABELS) {
    if (parts.length >= max) break;
    const n = counts[key];
    if (typeof n !== "number") continue; // omitted by the hub = not reported, not zero
    parts.push(`${n} ${n === 1 ? singular : plural}`);
  }
  return parts.join(" · ");
}

/** Every reported counter, labelled — the detail view's full list (no cap). */
export function reportCounts(
  report: LicenseReport | null | undefined,
): Array<{ label: string; value: number }> {
  const counts = report?.counts;
  if (!counts) return [];
  return COUNT_LABELS.filter(([key]) => typeof counts[key] === "number").map(
    ([key, singular, plural]) => ({
      label: counts[key] === 1 ? singular : plural,
      value: counts[key],
    }),
  );
}

function daysSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

/** Days since the install last reported, or `null` when it never has. DISTINCT from a
 *  stale heartbeat — a hub can heartbeat happily while reporting nothing. */
export function daysSinceReport(lic: PlatformLicense, now = new Date()): number | null {
  return daysSince(lic.last_report_at, now);
}

/** The heartbeat runs every 6h, so this is ~8 missed beats — late enough that a blip or a
 *  restart has passed, early enough to act on. */
export const OFFLINE_AFTER_DAYS = 2;

export type LicenseAttentionKind = "license_offline" | "license_silent";

/**
 * Is this on-prem install worth the superadmin's attention, and WHY?
 *
 * The two failure modes are genuinely different and must not be collapsed:
 * **offline** (the heartbeat itself stopped — the install is unreachable, or its license
 * is about to go stale) versus **silent** (it heartbeats fine but tells us nothing about
 * itself — almost always a hub predating L1, i.e. an upgrade to chase, not an outage).
 * Reporting one as the other would send the operator after the wrong problem.
 *
 * A REVOKED key and a never-activated one both return `null`: the first is a deliberate
 * decision and the second is an unused key, so neither is an incident.
 */
export function licenseAttention(
  lic: PlatformLicense,
  now = new Date(),
): { kind: LicenseAttentionKind; detail: string } | null {
  if (lic.status !== "active" || !lic.activated_at) return null;

  const beat = daysSince(lic.heartbeat_at, now);
  if (beat === null || beat >= OFFLINE_AFTER_DAYS) {
    return {
      kind: "license_offline",
      detail: beat === null ? "Mai vista online" : `Offline da ${beat}g`,
    };
  }
  const reported = daysSinceReport(lic, now);
  if (reported === null || reported >= OFFLINE_AFTER_DAYS) {
    return { kind: "license_silent", detail: "Non invia dati · versione precedente?" };
  }
  return null;
}
